import React, { useRef, useState, useEffect } from "react";

import { useIntl } from "react-intl";
import styled from "styled-components";

import { clearFormRef } from "utils/helpers";
import { useWindowWidth } from "hooks";
import FormHOC from "hoc/FilltersForm";
import CustomSelfClearedForm from "components/client/Form/CustomSelfClearedForm";
import { BaseInputField, BaseSelectPicker } from "components/base/BaseForm";
import CustomField from "components/client/Form/CustomField/CustomField";
import CheckboxFullControl from "components/Form/CheckboxFullControl";
import { FlexGrid, FlexGridItem } from "components/base/FlexGrid";
import m from "definedMessages";


import { CheckPicker } from "rsuite";

export const normalizeAccessesFilterValue = (filterValue = {}) => ({
    ...(filterValue || {}),
    sp_key_list: Array.isArray(filterValue?.sp_key_list) ? filterValue.sp_key_list : [],
});

export default FormHOC(({
    loading,
    service,
    savedFilter,
    filterState,
    defaultFilter,
    filtersModel,
    setFilterChanged,
    onChangeFilter,
    prices = [],
    currency_list,
    accountInfo,
}) => {
    const formRef = useRef(null);
    const filterTimeoutRef = useRef(null);

    const normalizedDefaultFilter = normalizeAccessesFilterValue(defaultFilter);
    const [filterValueState, setFilterValueState] = useState(() => (
        normalizeAccessesFilterValue(filterState || defaultFilter)
    ));
    const [noNumberDebounce, setNoNumberDebounce] = useState(false);
    const [noNumberValue, setNoNumberValue] = useState(false);

    const [noCapacityAccessesDebounce, setNoCapacityAccessesDebounce] = useState(false);
    const [noCapacityAccessesValue, setNoCapacityAccessesValue] = useState(false);

    useEffect(() => {
        if (filterTimeoutRef.current) {
            clearTimeout(filterTimeoutRef.current);
            filterTimeoutRef.current = null;
        }

        clearFormRef(formRef, true);

        setNoNumberValue(false);
        setNoCapacityAccessesValue(false);
        onChangeFilter(defaultFilter, true);
        setFilterValueState(normalizeAccessesFilterValue(defaultFilter));
        setFilterChanged(false);
    }, [service]);

    useEffect(() => {
        return () => {
            if (filterTimeoutRef.current) {
                clearTimeout(filterTimeoutRef.current);
            }
        };
    }, []);

    const { formatMessage } = useIntl();
    const windowWidth = useWindowWidth();

    const onSubmit = (data, checkedData) => {
        onChangeFilter({
            ...data,
            sp_key_list: data.sp_key_list && data.sp_key_list.length
                ? data.sp_key_list
                : null,
        });
        setFilterChanged(true);
    };

    const priceList = prices ? prices.map(({ sp_key: value, name: label }) => ({ value, label })) : [];
    const curList = currency_list ? currency_list.map(({ cur_key: value, name: label }) => ({ value, label })) : [];

    return (
        <CustomSelfClearedForm
            ref={formRef}
            clearOn={service}
            model={filtersModel}
            formValue={filterValueState}
            formDefaultValue={normalizedDefaultFilter}
            onChange={(data) => {
                const normalizedData = normalizeAccessesFilterValue(data);

                setFilterValueState(normalizedData);

                if (filterTimeoutRef.current) {
                    clearTimeout(filterTimeoutRef.current);
                }

                filterTimeoutRef.current = setTimeout(() => {
                    const checkedData = filtersModel.check(normalizedData);

                    onSubmit(normalizedData, checkedData);

                    setNoNumberDebounce(false);
                    setNoCapacityAccessesDebounce(false);

                    filterTimeoutRef.current = null;
                }, 1000);
            }}
            fluid
        >
            <FlexGrid align="top">
                <FlexGridItem width={windowWidth > 767 ? "180px" : "100%"}>
                    <CustomField
                        data={priceList}
                        accepter={CheckPicker}
                        readOnly={loading}
                        searchable={false}
                        name="sp_key_list"
                        cleanable={false}
                        virtualized={false}
                    />
                </FlexGridItem>
                {accountInfo?.is_test &&
                    <FlexGridItem>
                        <CustomField
                            data={curList}
                            accepter={BaseSelectPicker}
                            readOnly={loading}
                            name="cur_key"
                            width={windowWidth > 767 ? service ? "360px" : "280px" : "100%"}
                            cleanable={false}
                            searchable={false}
                        />
                    </FlexGridItem>
                }
                <FlexGridItem width={windowWidth > 767 ? service ? "360px" : "320px" : "100%"}>
                    <CustomField
                        name={service ? "str" : "origin"}
                        accepter={BaseInputField}
                        readOnly={loading}
                        errorPlacement="topEnd"
                        placeholder={formatMessage(m.destinationFilterMinCharacter)}
                    />
                </FlexGridItem>
                <FlexGridItem width={windowWidth > 767 ? service ? "360px" : "320px" : "100%"}>
                    <CustomField
                        name={service ? "str2" : "destination"}
                        accepter={BaseInputField}
                        readOnly={loading}
                        errorPlacement="topEnd"
                        placeholder={formatMessage(m.customDestinationOnlyFilterMinCharacter, { destination: "destination" })}
                    />
                </FlexGridItem>
                {!service && <FlexGridItem width={windowWidth > 767 ? service ? "380px" : "320px" : "100%"}>
                    <CustomField
                        name="message"
                        accepter={BaseInputField}
                        readOnly={loading}
                        errorPlacement="topEnd"
                        placeholder={formatMessage(m.message)}
                    />
                </FlexGridItem>}
            </FlexGrid>
            <SecondFlexGrid align="top">
                <FlexGridItem className={noNumberDebounce ? "disabled" : ""}>
                    <CustomField
                        accepter={CheckboxFullControl}
                        onChange={(check) => {
                            setNoNumberDebounce(true);
                            setNoNumberValue(check);
                        }}
                        value={noNumberValue}
                        disabled={noNumberDebounce}
                        name="no_number"
                    >
                        {formatMessage(m.hidePrefixes)}
                    </CustomField>
                </FlexGridItem>
                {!service && <FlexGridItem className={noCapacityAccessesDebounce ? "disabled" : ""}>
                    <CustomField
                        accepter={CheckboxFullControl}
                        name="show_accesses_without_capacity"
                        onChange={(check) => {
                            setNoCapacityAccessesDebounce(true);
                            setNoCapacityAccessesValue(check);
                        }}
                        value={noCapacityAccessesValue}
                        disabled={noCapacityAccessesDebounce}
                    >
                        {formatMessage(m.noCapacityAccesses)}
                    </CustomField>
                </FlexGridItem>}
            </SecondFlexGrid>
        </CustomSelfClearedForm>
    );
});

const SecondFlexGrid = styled(FlexGrid)`
    && {
        margin-top: 10px;

        .rs-flex-box-grid-item {
            padding: 0px !important;
        }
        .rs-flex-box-grid-item.disabled {
            opacity: 0.3;
            cursor: not-allowed;
            pointer-events: none;
        }
    }
`;
